import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReportsApi } from '@/hooks'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { downloadFileFromUrl } from '@/utils/downloadFileFromUrl'

type UseDownloadEmailExportParams = {
    exportId: string | undefined
    onComplete: () => void
}

/**
 * Orchestrates the download of a report export triggered by a link
 * from an email notification.
 *
 * This is distinct from the in-app export hooks (useExport*Report),
 * which generate exports on-the-fly from report data. This hook
 * downloads a pre-generated export file via a short-lived S3 URL
 * provided by the backend.
 *
 * @example
 * useDownloadEmailExport({
 *   exportId: searchParams.downloadExportId, // from URL
 *   onComplete: () => clearDownloadExportIdFromUrl(),
 * })
 */
export const useDownloadEmailExport = ({
    exportId,
    onComplete,
}: UseDownloadEmailExportParams) => {
    const { t } = useTranslation('translations')
    const addAlert = useToastStore((state) => state.addAlert)
    const hasTriggered = useRef(false)

    const { useGetReportExportDownloadUrl } = useReportsApi()

    const { data, isError } = useGetReportExportDownloadUrl(exportId ?? '', {
        enabled: !!exportId && !hasTriggered.current,
    })

    // Reset the guard when exportId is cleared, making the hook ready
    // for the next trigger without requiring a remount.
    useEffect(() => {
        if (!exportId) {
            hasTriggered.current = false
        }
    }, [exportId])

    useEffect(() => {
        if (!exportId || hasTriggered.current) {
            return
        }

        if (isError) {
            hasTriggered.current = true
            addAlert({
                variant: 'error',
                title: t('reporting.email_export.error_toast.title'),
                description: t(
                    'reporting.email_export.error_toast.description'
                ),
            })
            onComplete()
            return
        }

        if (data?.downloadUrl) {
            hasTriggered.current = true
            downloadFileFromUrl(data.downloadUrl)
            addAlert({
                variant: 'success',
                title: t('reporting.email_export.success_toast.title'),
                description: t(
                    'reporting.email_export.success_toast.description'
                ),
            })
            onComplete()
        }
    }, [exportId, data, isError, addAlert, onComplete, t])
}
